import { AdvancedMarker } from "@vis.gl/react-google-maps";
import { memo } from "react";

export type MarkerType = "dropoff" | "pickup";

export interface MarkerProps {
  alt?: string;
  className?: string;
  icon?: string;
  onClick?: (event: React.MouseEvent) => void;
  onMouseEnter?: (event: React.MouseEvent) => void;
  onMouseLeave?: (event: React.MouseEvent) => void;
  opacity?: number;
  position: { lat: number; lng: number };
  routePosition?: number | null;
  routePositionColor: string;
  scale?: number;
  type: MarkerType;
}

const MARKER_CONFIG = {
  dropoff: {
    backgroundColor: "#EF4444", // Red
    letter: "D",
  },
  pickup: {
    backgroundColor: "#10B981", // Green
    letter: "P",
  },
} as const;

export const Marker = memo(
  ({
    alt,
    className,
    icon,
    opacity = 1,
    onClick,
    onMouseEnter,
    onMouseLeave,
    position,
    routePosition,
    routePositionColor,
    scale = 1,
    type,
  }: MarkerProps) => {
    const config = MARKER_CONFIG[type];
    const markerSize = 18 * scale;
    const imageHeight = Math.round(markerSize * 1.2);
    const imageWidth = Math.round(markerSize);

    const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
      const target = e.target as HTMLElement;
      target.style.display = "none";
      const fallback = target.nextElementSibling as HTMLElement;
      if (fallback) fallback.style.display = "flex";
    };

    return (
      <AdvancedMarker position={position}>
        <div
          className={className}
          style={{
            cursor: onClick ? "pointer" : "default",
            opacity,
            position: "relative",
            transform: `scale(${scale})`,
            transition: "all 0.2s ease",
          }}
          onClick={onClick}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        >
          {icon && (
            <img
              src={icon}
              alt={alt || `${type} marker`}
              style={{
                display: "block",
                height: `${imageHeight}px`,
                width: `${imageWidth}px`,
              }}
              onError={handleImageError}
            />
          )}

          <div
            style={{
              alignItems: "center",
              backgroundColor: config.backgroundColor,
              border: "2px solid white",
              borderRadius: "50%",
              boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
              color: "white",
              display: icon ? "none" : "flex",
              fontSize: `${Math.max(8, markerSize * 0.4)}px`,
              fontWeight: "bold",
              height: `${markerSize + 5}px`,
              justifyContent: "center",
              width: `${markerSize + 5}px`,
            }}
          >
            {config.letter}
          </div>

          {routePosition && (
            <div
              style={{
                alignItems: "center",
                backgroundColor: routePositionColor,
                border: "2px solid white",
                borderRadius: "50%",
                boxShadow: "0 1px 4px rgba(0,0,0,0.4)",
                color: "white",
                display: "flex",
                fontSize: `${Math.max(7, markerSize * 0.5)}px`,
                fontWeight: "bold",
                height: `${Math.round(markerSize * 0.9)}px`,
                justifyContent: "center",
                position: "absolute",
                right: "-25%",
                top: "-25%",
                width: `${Math.round(markerSize * 0.9)}px`,
                zIndex: 1,
              }}
            >
              {routePosition}
            </div>
          )}
        </div>
      </AdvancedMarker>
    );
  },
);

Marker.displayName = "Marker";
